import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
// Removed react-icons/fa imports for amenities; now using PNGs
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import ImageModal from "../../../components/ImagePreviewModal";
import { SeatingSelectionStep } from "./SeatingSelectionStep";
import { EventDateSelectionStep } from "./EventDateSelectionStep";
import { HotelSelectionStep } from "./HotelSelectionStep";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-regular-svg-icons";
import {
  calculatePricingBreakdown,
  getDisplayPrice,
} from "../../../utils/pricingUtils";
import ImagePreviewModalWithZoom from "../../../components/ImagePreviewModalWithZoom";
import { useTranslation } from "react-i18next";
import { defaultCenter } from "../../Home/_components/constants";

function InquiryForm({ event, hotels, setHotels }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [inquiryOpen, setInquiryOpen] = useState(true);
  const [inquiryMapOpen, setInquiryMapOpen] = useState(true);
  const [inquiryForm, setInquiryForm] = useState({
    customerName: "",
    email: "",
    phone: "",
    eventDates: [],
    eventDateCost: 0,
    customStartDate: "",
    customEndDate: "",
    seatingPreference: "",
    numberOfGuests: 2,
    additionalGuestNames: [],
    hotelPreference: [],
    tripDuration: null,
  });
  const [isCustomInquiry, setIsCustomInquiry] = useState(false);
  // Check if the event has all required components for direct booking
  const [isBookable, setIsBookable] = useState(true);
  const [totalPrice, setTotalPrice] = useState(event?.basePrice || 0);
  const [inquiryError, setInquiryError] = useState("");
  const [inquirySuccess, setInquirySuccess] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const formRef = useRef(null);
  const mapRef = useRef(null); // Reference to the GoogleMap component
  const [hotelImageIndices, setHotelImageIndices] = useState({});
  const [, setRoomImageIndices] = useState({});
  const [venueCoords, setVenueCoords] = useState(null);
  const [selectedVenueMarker, setSelectedVenueMarker] = useState(false);
  const [hotelCoords, setHotelCoords] = useState({});
  const [hotelDistances, setHotelDistances] = useState({});
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [mapError, setMapError] = useState(null);
  const [selectedRooms, setSelectedRooms] = useState([]);

  const [hotelPricesPerPerson, setHotelPricesPerPerson] = useState(0);
  const [hotelPricesPerPersonStep2, setHotelPricesPerPersonStep2] = useState(0);
  const [selectedHotelMarker, setSelectedHotelMarker] = useState(null);
  const [hasBasePrice, setHasBasePrice] = useState(false);
  const [currentHotelIndex, setCurrentHotelIndex] = useState(0);
  const { t } = useTranslation();

  // Scroll the booking/request section into view (used when a hotel on the map is clicked)
  const scrollToBookingSection = useCallback(() => {
    // Ensure the booking request panel is open so its header is visible
    try {
      setInquiryOpen(true);
      // keep the map toggled state as-is, but ensure the form is visible
      if (formRef && formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        // Compensate for any fixed header
        setTimeout(() => {
          window.scrollBy({ top: -75, left: 0, behavior: "smooth" });
        }, 300);
      }
    } catch (e) {
      // no-op
    }
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBxuKcDrcwD7JX2PY4LDH_EODW_5nWxIYs",
  });

  const mapContainerStyle = {
    height: "300px",
    width: "100%",
  };

  // Default zoom level when there are no hotels (zoomed out a bit)
  const defaultZoomNoHotels = 12;
  // Amenity icon mapping - use PNGs for consistency with email
  const amenityIcons = {
    WiFi: "/images/wifi.png",
    Pool: "/images/pool.png",
    Spa: "/images/spa.png",
    Restaurant: "/images/restaurant.png",
    Parking: "/images/Parking.png",
    "Nearby Parking": "/images/nearby-parking.png",
    Bar: "/images/bar.png",
    Breakfast: "/images/breakfast1.png",
    "Luggage-service": "/images/luggage-service.png",
  };

  // Fit the map bounds to include all markers (venue and hotels)
  const fitMapBounds = useCallback(() => {
    // Default coordinates (e.g., a fallback location like New York City)
    const defaultCoords = { lat: 40.7128, lng: -74.006 };
    if (!mapRef.current || !isLoaded) return;

    const bounds = new window.google.maps.LatLngBounds();

    // Include venue coordinates if available
    if (venueCoords) {
      bounds.extend(
        new window.google.maps.LatLng(venueCoords.lat, venueCoords.lng)
      );
    }

    // Include hotel coordinates if available
    filteredHotels.forEach((hotel) => {
      if (hotelCoords[hotel._id]) {
        bounds.extend(
          new window.google.maps.LatLng(
            hotelCoords[hotel._id].lat,
            hotelCoords[hotel._id].lng
          )
        );
      }
    });

    // If there are no markers, use default coordinates
    if (bounds.isEmpty()) {
      mapRef.current.setCenter(
        new window.google.maps.LatLng(defaultCoords.lat, defaultCoords.lng)
      );
      mapRef.current.setZoom(defaultZoomNoHotels);
      return;
    }

    // If there are no hotels (only venue), set a fixed zoom level
    if (venueCoords && filteredHotels.length === 0) {
      mapRef.current.setCenter(
        new window.google.maps.LatLng(venueCoords.lat, venueCoords.lng)
      );
      mapRef.current.setZoom(defaultZoomNoHotels);
    } else {
      // Otherwise, fit the bounds to include all markers
      mapRef.current.fitBounds(bounds);
    }
  }, [venueCoords, hotelCoords, filteredHotels, isLoaded]);

  useEffect(() => {
    const initialHotelIndices = {};
    const initialRoomIndices = {};
    if (hotels) {
      hotels.forEach((hotel) => {
        const allImages = [
          ...(hotel.images || []),
          ...hotel.roomTypes.flatMap((rt) => rt.images || []),
        ];
        initialHotelIndices[hotel._id] = allImages.length > 0 ? 0 : -1;
        hotel.roomTypes.forEach((rt) => {
          initialRoomIndices[`${hotel._id}:${rt.type}`] =
            rt.images && rt.images.length > 0 ? 0 : -1;
        });
      });
    }
    setHotelImageIndices(initialHotelIndices);
    setRoomImageIndices(initialRoomIndices);
  }, [hotels]);

  useEffect(() => {
    if (event) {
      const hasDate = event.date_times.length > 0;
      event.basePrice > 0 && setHasBasePrice(true);
      const eventIsBookable = hasDate;
      setIsBookable(eventIsBookable);

      if (!eventIsBookable) {
        setIsCustomInquiry(true);
      }
    }
  }, [event]);

  useEffect(() => {
    let startDate, endDate, nights;

    if (
      isCustomInquiry &&
      inquiryForm.customStartDate &&
      inquiryForm.customEndDate
    ) {
      startDate = new Date(inquiryForm.customStartDate);
      endDate = new Date(inquiryForm.customEndDate);
      nights = (endDate - startDate) / (1000 * 60 * 60 * 24);
    } else if (inquiryForm.eventDates.length > 0 && event) {
      const eventDate = new Date(inquiryForm.eventDates[0]);
      const isUSAVenue = event.venue.country.toLowerCase() === "united states";
      const daysBefore = isUSAVenue ? 2 : 1;
      const daysAfter = isUSAVenue ? 2 : 1;

      startDate = new Date(eventDate);
      startDate.setDate(eventDate.getDate() - daysBefore);

      endDate = new Date(eventDate);
      endDate.setDate(eventDate.getDate() + daysAfter);

      nights = (endDate - startDate) / (1000 * 60 * 60 * 24);
    }

    if (startDate && endDate && nights >= 0) {
      const startDay = startDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      const endDay = endDate.toLocaleDateString("en-US", { weekday: "long" });
      const year = startDate.getFullYear();
      setInquiryForm((prev) => ({
        ...prev,
        tripDuration: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          year: year,
          nights,
          startDay,
          endDay,
        },
      }));
    } else {
      setInquiryForm((prev) => ({ ...prev, tripDuration: null }));
    }
  }, [
    inquiryForm.eventDates,
    inquiryForm.customStartDate,
    inquiryForm.customEndDate,
    isCustomInquiry,
    event,
  ]);
  useEffect(() => {
    const pricing = calculatePricingBreakdown({
      event,
      inquiryForm,
      isCustomInquiry,
      filteredHotels,
    });

    // Set state based on current step
    if (inquiryForm.hotelPreference.length === 0) {
      // Before hotel selection - show base price per person (without hotel costs)
      const baseWithoutHotel =
        pricing.baseTotal + pricing.eventDateCost + pricing.seatingCost;
      setHotelPricesPerPersonStep2(
        baseWithoutHotel / Math.max(inquiryForm.numberOfGuests, 1)
      );
      setHotelPricesPerPerson(0);
      setTotalPrice(baseWithoutHotel);
    } else {
      // After hotel selection - show final price per person
      setHotelPricesPerPerson(pricing.pricePerPerson);
      setHotelPricesPerPersonStep2(0);
      setTotalPrice(pricing.finalTotal);
    }
  }, [event, inquiryForm, isCustomInquiry, filteredHotels, step]);

  // Compute filteredHotels when hotels or numberOfGuests changes
  useEffect(() => {
    if (hotels) {
      // const filtered = hotels.filter(hotel => {
      //   return hotel.roomTypes.some(roomType => roomType.capacity >= inquiryForm.numberOfGuests);
      // });
      setFilteredHotels(hotels);
    } else {
      setFilteredHotels([]);
    }
  }, [hotels, inquiryForm.numberOfGuests]);

  // Geocode venue and hotel addresses for map display
  useEffect(() => {
    if (!isLoaded || !event || !event.venue) return;

    const geocoder = new window.google.maps.Geocoder();

    // Geocode venue address
    const venueAddress = `${event.venue.streetAddress}, ${event.venue.city}, ${event.venue.country}`;
    geocoder.geocode({ address: venueAddress }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        setVenueCoords({ lat: location.lat(), lng: location.lng() });
      } else {
        console.error("Geocoding failed for venue:", status);
        setVenueCoords(null);
        setMapError(`Failed to geocode venue address: ${status}`);
      }
    });

    // Geocode hotel addresses (even if filteredHotels is empty, proceed with venue)
    const newHotelCoords = {};
    filteredHotels.forEach((hotel) => {
      const hotelAddress = `${hotel.address}, ${hotel.city}`;
      geocoder.geocode({ address: hotelAddress }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          newHotelCoords[hotel._id] = {
            lat: location.lat(),
            lng: location.lng(),
          };
          setHotelCoords((prev) => ({
            ...prev,
            [hotel._id]: { lat: location.lat(), lng: location.lng() },
          }));
        } else {
          console.error(`Geocoding failed for hotel ${hotel._id}:`, status);
          newHotelCoords[hotel._id] = null;
        }
      });
    });
  }, [isLoaded, event, filteredHotels, t]);
  // useEffect(() => {
  //   if (step < 3) {
  //     setInquiryForm((prev) => ({
  //       ...prev,
  //       hotelPreference: [],
  //     }));
  //   }
  // }, [step]);
  // // Adjust map bounds whenever venueCoords or hotelCoords change
  useEffect(() => {
    fitMapBounds();
  }, [venueCoords, hotelCoords, fitMapBounds]);

  // Calculate distances between venue and hotels using Distance Matrix API
  useEffect(() => {
    if (!isLoaded || !event || !event.venue || filteredHotels.length === 0)
      return;

    const venueAddress = `${event.venue.streetAddress}, ${event.venue.city}, ${event.venue.country}`;
    const hotelAddresses = filteredHotels.map(
      (hotel) => `${hotel.address}, ${hotel.city}`
    );

    const distanceService = new window.google.maps.DistanceMatrixService();
    distanceService.getDistanceMatrix(
      {
        origins: [venueAddress],
        destinations: hotelAddresses,
        travelMode: window.google.maps.TravelMode.WALKING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK" && response.rows[0]) {
          const distances = {};
          response.rows[0].elements.forEach((element, index) => {
            const hotelId = filteredHotels[index]._id;
            if (element.status === "OK") {
              distances[hotelId] = element.distance.text;
            } else {
              distances[hotelId] = t("EventPage.distanceUnavailable");
            }
          });
          setHotelDistances(distances);
        } else {
          const distances = {};
          filteredHotels.forEach((hotel) => {
            distances[hotel._id] = t("EventPage.distanceUnavailable");
          });
          setHotelDistances(distances);
        }
      }
    );
  }, [isLoaded, event, filteredHotels]);

  useEffect(() => {
    if (
      ((inquiryForm.eventDates.length > 0 && !isCustomInquiry) ||
        (inquiryForm.customStartDate &&
          inquiryForm.customEndDate &&
          isCustomInquiry)) &&
      inquiryForm.hotelPreference.length > 0
    ) {
      setInquiryError("");
      return;
    } else if (
      (!(inquiryForm.eventDates.length > 0 && !isCustomInquiry) ||
        !(
          inquiryForm.customStartDate &&
          inquiryForm.customEndDate &&
          isCustomInquiry
        )) &&
      inquiryForm.hotelPreference.length > 0
    ) {
      setInquiryError("");
      return;
    } else if (inquiryForm.seatingPreference) {
      setStep(3);
      return;
    } else if (
      inquiryForm.eventDates.length > 0 ||
      (inquiryForm.customStartDate && inquiryForm.customEndDate)
    ) {
      setStep(2);
      return;
    }
  }, [inquiryForm, isCustomInquiry]);

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm((prev) => ({
      ...prev,
      [name]: name === "numberOfGuests" ? parseInt(value) || 1 : value,
    }));
  };

  const handleSelectHotelById = (hotelId) => {
    const index = filteredHotels.findIndex((h) => h._id === hotelId);
    if (index !== -1) setCurrentHotelIndex(index);
  };

  const handleEventDatesChange = (value) => {
    setInquiryForm((prev) => ({
      ...prev,
      eventDates: value?.date ? [value.date] : [],
      eventDateCost: parseFloat(value?.cost) || 0,
    }));
  };

  const handleSeatingPreferenceChange = (value) => {
    setInquiryForm((prev) => ({ ...prev, seatingPreference: value }));
  };

  const getTotalSelectedRoomsCapacity = () =>
    selectedRooms.reduce((sum, room) => {
      return sum + (room.capacity || 0);
    }, 0);

  // Function to determine button text based on current step and form state
  const getButtonText = () => {
    if (
      isCustomInquiry ||
      !isBookable ||
      !hasBasePrice ||
      filteredHotels.length === 0
    ) {
      if (
        (!inquiryForm.customEndDate || !inquiryForm.customStartDate) &&
        !inquiryForm.tripDuration
      ) {
        return t("EventPage.selectYourPreferences");
      }

      if (
        (inquiryForm.customEndDate && inquiryForm.customStartDate) ||
        inquiryForm.tripDuration
      ) {
        if (
          hasValidSeatingPlan &&
          inquiryForm.seatingPreference &&
          inquiryForm.hotelPreference.length > 0
        ) {
          return t("EventPage.sendRequest");
        } else if (
          hasValidSeatingPlan &&
          inquiryForm.seatingPreference &&
          inquiryForm.hotelPreference.length === 0
        ) {
          return t("EventPage.chooseYourHotel");
        } else if (hasValidSeatingPlan) {
          return t("EventPage.chooseYourSeating");
        } else if (filteredHotels.length === 0) {
          return t("EventPage.sendRequest");
        } else {
          return t("EventPage.chooseYourHotel");
        }
      }
      return t("EventPage.sendRequest");
    }

    if (!inquiryForm.tripDuration) {
      return t("EventPage.selectYourPreferences");
    }
    if (
      inquiryForm.tripDuration &&
      inquiryForm.seatingPreference &&
      step === 3 &&
      !hasBasePrice
    ) {
      return t("EventPage.sendRequest");
    }
    if (
      inquiryForm.tripDuration &&
      inquiryForm.seatingPreference &&
      inquiryForm.hotelPreference.length > 0
    ) {
      return t("EventPage.bookNow");
    }
    if (
      inquiryForm.tripDuration &&
      inquiryForm.seatingPreference &&
      step === 3 &&
      inquiryForm.hotelPreference.length === 0
    ) {
      return t("EventPage.chooseYourHotel");
    }
    if (
      inquiryForm.tripDuration &&
      inquiryForm.seatingPreference &&
      filteredHotels.length === 0
    ) {
      return t("EventPage.sendRequest");
    }

    if (
      inquiryForm.tripDuration &&
      inquiryForm.seatingPreference &&
      step === 3
    ) {
      return t("EventPage.bookNow");
    }

    if (inquiryForm.tripDuration && inquiryForm.seatingPreference) {
      return t("EventPage.chooseYourHotel");
    }

    if (inquiryForm.tripDuration) {
      if (hasValidSeatingPlan) {
        return t("EventPage.chooseYourSeating");
      } else {
        return t("EventPage.chooseYourHotel");
      }
    }

    return t("EventPage.bookNow");
  };

  const calculateTripDetails = (eventDate) => {
    const date = new Date(eventDate);
    const isUSAVenue = event.venue.country.toLowerCase() === "united states";
    const daysBefore = isUSAVenue ? 2 : 1;
    const daysAfter = isUSAVenue ? 2 : 1;

    const startDate = new Date(date);
    startDate.setDate(date.getDate() - daysBefore);

    const endDate = new Date(date);
    endDate.setDate(date.getDate() + daysAfter);

    const startDay = startDate.toLocaleDateString("en-US", { weekday: "long" });
    const endDay = endDate.toLocaleDateString("en-US", { weekday: "long" });

    // Format as DD.MM.YYYY
    const formatDate = (d) => {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    return {
      tripDates: `${formatDate(startDate)} - ${formatDate(endDate)}`,
      weekdays: `${startDay}-${endDay}`,
    };
  };

  const validateStep1 = () => {
    if (isCustomInquiry) {
      if (!inquiryForm.customStartDate || !inquiryForm.customEndDate) {
        setInquiryError(t("EventPage.customStartAndEndDate"));
        return false;
      }
      const start = new Date(inquiryForm.customStartDate);
      const end = new Date(inquiryForm.customEndDate);
      if (end <= start) {
        setInquiryError(t("EventPage.endDateAfterStartDate"));
        return false;
      }
    } else if (inquiryForm.eventDates.length === 0) {
      setInquiryError(t("EventPage.selectEventDate"));
      return false;
    }
    if (!inquiryForm.numberOfGuests || inquiryForm.numberOfGuests < 1) {
      setInquiryError(t("EventPage.specifyGuests"));
      return false;
    }
    setInquiryError("");
    return true;
  };

  const validateStep2 = () => {
    // If there's no valid seating plan, automatically pass validation
    if (!hasValidSeatingPlan) {
      setInquiryError("");
      return true;
    }

    // Otherwise, require a seating preference
    if (!inquiryForm.seatingPreference) {
      setInquiryError(t("EventPage.selectSeatingCategory"));
      return false;
    }

    setInquiryError("");
    return true;
  };

  const validateStep3 = () => {
    // If there are no hotels available or the venue doesn't have a default hotel,
    // allow the user to proceed without selecting a hotel
    if (
      filteredHotels.length === 0 ||
      !event.venue?.defaultHotelRoom?.hotelId
    ) {
      setInquiryError("");
      return true;
    }

    // Otherwise, require a hotel selection
    if (inquiryForm.hotelPreference.length === 0) {
      setInquiryError(t("EventPage.selectHotelAndRoom"));
      return false;
    }
    if (!(getTotalSelectedRoomsCapacity() >= inquiryForm.numberOfGuests)) {
      setInquiryError(
        t("EventPage.roomCapacityError") +
          getTotalSelectedRoomsCapacity() +
          getTotalSelectedRoomsCapacity() !==
          1
          ? t("EventPage.guests")
          : t("EventPage.guest") +
              t("EventPage.butYouHave") +
              inquiryForm.numberOfGuests !==
            1
          ? t("EventPage.guests")
          : t("EventPage.guest").t("EventPage.adjustRoomSelection")
      );
      return false;
    }

    setInquiryError("");
    return true;
  };

  // Check if venue has a valid seating plan
  const hasValidSeatingPlan = useMemo(() => {
    return (
      event?.venue?.seatingPlan &&
      event.venue.seatingPlan.length > 0 &&
      event.venue.seatingPlan.some(
        (plan) => plan.category && plan.category.trim() !== ""
      )
    );
  }, [event]);

  const handleTabClick = (targetStep) => {
    if (targetStep === step) return;

    setStep(targetStep);
    setInquiryError("");
    return;
  };
  // const handleTabClick = (targetStep) => {
  //   if (targetStep === step) return;

  //   // Always allow going back (but only to step 3 max since step 4 doesn't exist anymore for bookable events)
  //   if (targetStep < step) {
  //     setStep(targetStep);
  //     setInquiryError("");
  //     return;
  //   }

  //   // Handle forward navigation with validation
  //   if (step === 1 && validateStep1()) {
  //     // If going to step 2 but no seating plan, skip to step 3
  //     if (targetStep === 2 && !hasValidSeatingPlan) {
  //       setStep(3);
  //     }
  //     // Normal navigation to step 2
  //     else if (targetStep === 2) {
  //       setStep(2);
  //     }
  //     // Going to step 3
  //     else if (targetStep === 3) {
  //       // If currently at step 1 and no seating plan, skip step 2 validation
  //       if (!hasValidSeatingPlan) {
  //         setStep(3);
  //       }
  //       // Normal path through step 2
  //       else if (validateStep2()) {
  //         setStep(3);
  //       }
  //     }
  //   }
  //   // From step 2, validate and move forward
  //   else if (step === 2 && validateStep2()) {
  //     if (targetStep === 3) setStep(3);
  //   }
  //   // From step 3, no tab navigation to step 4 since we redirect to booking page
  // };
  const handleNext = () => {
    const bookingData = {
      event,
      inquiryForm,
      totalPrice,
      selectedRooms,
      hotelPricesPerPerson,
      hotels: filteredHotels,
      isCustomInquiry: isCustomInquiry,
      hasBasePrice: hasBasePrice,
    };
    const bookingDataInquiry = {
      event,
      inquiryForm,
      totalPrice,
      selectedRooms,
      hotelPricesPerPerson,
      hotels: filteredHotels,
      isCustomInquiry: true,
      hasBasePrice: hasBasePrice,
    };

    if (step === 1 && validateStep1()) {
      // If no seating plan, skip step 2
      if (!hasValidSeatingPlan && filteredHotels.length === 0) {
        navigate("/booking", { state: { bookingData: bookingDataInquiry } });
      } else if (!hasValidSeatingPlan) {
        setStep(3);
      } else if (
        inquiryForm.tripDuration &&
        inquiryForm.seatingPreference &&
        inquiryForm.hotelPreference.length === 0
      ) {
        setStep(3);
      } else if (
        inquiryForm.tripDuration &&
        inquiryForm.seatingPreference &&
        inquiryForm.hotelPreference.length > 0
      ) {
        if (validateStep1() && validateStep2() && validateStep3()) {
          if (isBookable && hasBasePrice) {
            navigate("/booking", { state: { bookingData } });
          } else {
            navigate("/booking", {
              state: { bookingData: bookingDataInquiry },
            });
          }
        } else {
          setStep(3);
          return;
        }
      } else {
        setStep(2);
      }
    } else if (step === 2 && validateStep2()) {
      setInquiryError("");
      if (
        inquiryForm.tripDuration &&
        inquiryForm.seatingPreference &&
        inquiryForm.hotelPreference.length === 0
      ) {
        setStep(3);
      } else if (
        inquiryForm.tripDuration &&
        inquiryForm.seatingPreference &&
        inquiryForm.hotelPreference.length > 0
      ) {
        if (validateStep1() && validateStep2() && validateStep3()) {
          if (isBookable && hasBasePrice) {
            navigate("/booking", { state: { bookingData } });
          } else {
            navigate("/booking", {
              state: { bookingData: bookingDataInquiry },
            });
          }
        } else {
          setStep(3);
          return;
        }
      }
      setStep(3);
    } else if (
      step === 3 &&
      !inquiryForm.seatingPreference &&
      hasValidSeatingPlan
    ) {
      setStep(2);
    } else if (validateStep1() && validateStep2() && validateStep3()) {
      if (isBookable && hasBasePrice) {
        navigate("/booking", { state: { bookingData } });
      } else {
        navigate("/booking", { state: { bookingData: bookingDataInquiry } });
      }
    }
  };

  const handleInquirySubmit = async () => {
    setInquiryError("");
    setInquirySuccess("");

    try {
      const requestData = {
        eventId: event._id,
        eventDates: isCustomInquiry
          ? [
              new Date(inquiryForm.customStartDate).toISOString(),
              new Date(inquiryForm.customEndDate).toISOString(),
            ]
          : inquiryForm.eventDates,
        seatingPreference: inquiryForm.seatingPreference,
        numberOfGuests: inquiryForm.numberOfGuests,
        hotelPreferences:
          Array.isArray(inquiryForm.hotelPreference) &&
          inquiryForm.hotelPreference.length > 0
            ? inquiryForm.hotelPreference.map((pref) => {
                const selected = selectedRooms.find(
                  (sel) =>
                    sel.hotelId === pref.hotelId &&
                    sel.roomType === pref.roomType
                );
                return {
                  hotel: pref.hotelId,
                  roomType: pref.roomType,
                  nights: inquiryForm.tripDuration?.nights,
                  hotelName: pref.hotelName,
                  quantity: selected ? selected.quantity : 1,
                };
              })
            : [],
        tripDuration: inquiryForm.tripDuration,
        customerName: inquiryForm.customerName,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        additionalGuestNames: inquiryForm.additionalGuestNames.filter(
          (name) => name
        ),
      };
      const response = await axios.post("/api/requests", requestData);

      // Prepare booking data for the thank you page
      const bookingData = {
        event,
        inquiryForm,
        totalPrice,
        selectedRooms,
        hotelPricesPerPerson,
        hotels: filteredHotels,
        isCustomInquiry: true,
      };

      // Navigate to thank you page
      navigate("/thank-you", {
        state: {
          bookingData,
          requestId: response.data.requestId,
        },
      });
    } catch (err) {
      setInquiryError(
        err.response?.data?.message || "Failed to submit inquiry"
      );
    }
  };

  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  return (
    <div ref={formRef} className="mt-4 border-t border-gray-300 pt-4">
      {step !== 2 && (
        <ImageModal
          open={modalOpen}
          image={modalImage}
          onClose={() => setModalOpen(false)}
        />
      )}
      {step === 2 && (
        <ImagePreviewModalWithZoom
          open={modalOpen}
          image={modalImage}
          onClose={() => setModalOpen(false)}
        />
      )}
      <button
        className="text-xl text-opera-red font-cinzel font-medium w-full text-left flex justify-between items-center focus:outline-none active:outline-none"
        onClick={() => setInquiryOpen(!inquiryOpen)}
      >
        {t("EventPage.bookingRequest")}
        {inquiryOpen ? (
          <svg
            className="w-6 h-6 text-black"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m5 15 7-7 7 7"
            />
          </svg>
        ) : (
          <svg
            className="w-6 h-6 text-black"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m19 9-7 7-7-7"
            />
          </svg>
        )}
      </button>
      {inquiryOpen && (
        <>
          <div className="mt-4 flex flex-col xl:flex-row gap-4">
            <div className="w-full xl:w-[75%]">
              {/* Progress Indicator: Tabs */}
              <div className="border-b border-gray-300 w-full">
                <div className="flex -mb-px">
                  {/* Always show Date tab */}

                  <button
                    onClick={() => handleTabClick(1)}
                    className={`px-5 w-[330px] lg:w-[420px] py-2 text-sm font-semibold transition-colors duration-200 focus:outline-none
                    ${
                      step === 1
                        ? "border border-gray-300 border-b-white rounded-t-md bg-white text-black relative"
                        : "border-b-2 border-transparent text-gray-500 hover:text-black hover:border-gray-300"
                    } `}
                  >
                    <div className="flex items-center place-self-center gap-3">
                      <img
                        src="/images/calendar.jpg"
                        alt="Calendar"
                        className="w-4 h-4 md:w-7 md:h-7 object-cover rounded"
                      />
                      <p className="hidden md:block">
                        {t("EventPage.chooseYourDates")}
                      </p>
                      <p className="block md:hidden">{t("EventPage.dates")}</p>
                    </div>
                  </button>

                  {/* Only show Category tab if venue has a seating plan */}
                  {event.venue?.seatingPlan &&
                    event.venue.seatingPlan.length > 0 &&
                    event.venue.seatingPlan.some(
                      (plan) => plan.category && plan.category.trim() !== ""
                    ) && (
                      <button
                        onClick={() => handleTabClick(2)}
                        className={`px-5 w-[330px] lg:w-[420px] py-2 text-sm font-semibold transition-colors duration-200 focus:outline-none
                      ${
                        step === 2
                          ? "border border-gray-300 border-b-white rounded-t-md bg-white text-black relative"
                          : "border-b-2 border-transparent text-gray-500 hover:text-black hover:border-gray-300"
                      } `}
                      >
                        <div className="flex items-center place-self-center gap-3">
                          <img
                            src="/images/tickets.jpg"
                            alt={t("EventPage.seating")}
                            className="w-4 h-4 md:w-7 md:h-7 object-cover rounded"
                          />
                          <p className="hidden md:block">
                            {t("EventPage.chooseYourSeating")}
                          </p>
                          <p className="block md:hidden">
                            {t("EventPage.seating")}
                          </p>
                        </div>
                      </button>
                    )}

                  {/* Always show Hotel tab */}
                  <button
                    onClick={() => handleTabClick(3)}
                    className={`px-5 w-[330px] lg:w-[420px] py-2 text-sm font-semibold transition-colors duration-200 focus:outline-none
                    ${
                      step === 3
                        ? "border border-gray-300 border-b-white rounded-t-md bg-white text-black relative"
                        : "border-b-2 border-transparent text-gray-500 hover:text-black hover:border-gray-300"
                    } `}
                  >
                    <div className="flex items-center place-self-center gap-3">
                      <img
                        src="/images/hotel.jpg"
                        alt={t("EventPage.hotel")}
                        className="w-4 h-4 md:w-7 md:h-7 object-cover rounded"
                      />
                      <p className="hidden md:block">
                        {t("EventPage.chooseYourHotel")}
                      </p>
                      <p className="block md:hidden">{t("EventPage.hotel")}</p>
                    </div>
                  </button>

                  {/* Always show Review tab */}
                  {/* <button
                  onClick={() => handleTabClick(4)}
                  className={`px - 4 py - 2 text - sm font - semibold transition - colors duration - 200 focus: outline - none
                    ${
      step === 4
        ? "border border-gray-300 border-b-white rounded-t-md bg-white text-opera-red relative"
        : "border-b-2 border-transparent text-gray-500 hover:text-black hover:border-gray-300"
    } `}
                >
                  Review and {isBookable ? "Book" : "Inquire"}
                </button> */}
                </div>
              </div>

              {/* Step 1: Choose Your Date */}
              {step === 1 && (
                <EventDateSelectionStep
                  event={event}
                  inquiryForm={inquiryForm}
                  inquiryError={inquiryError}
                  isCustomInquiry={isCustomInquiry}
                  setIsCustomInquiry={setIsCustomInquiry}
                  handleInquiryChange={handleInquiryChange}
                  handleEventDatesChange={handleEventDatesChange}
                  calculateTripDetails={calculateTripDetails}
                  formRef={formRef}
                />
              )}

              {/* Step 2: Choose Your Category */}
              {step === 2 && (
                <SeatingSelectionStep
                  event={event}
                  inquiryForm={inquiryForm}
                  inquiryError={inquiryError}
                  isCustomInquiry={isCustomInquiry}
                  handleSeatingPreferenceChange={handleSeatingPreferenceChange}
                  setInquiryForm={setInquiryForm}
                  openModal={openModal}
                />
              )}

              {/* Step 3: Select Hotel */}
              {step === 3 && (
                <HotelSelectionStep
                  inquiryError={inquiryError}
                  filteredHotels={filteredHotels}
                  amenityIcons={amenityIcons}
                  hotelDistances={hotelDistances}
                  hotelImageIndices={hotelImageIndices}
                  inquiryForm={inquiryForm}
                  setInquiryForm={setInquiryForm}
                  selectedRooms={selectedRooms}
                  setSelectedRooms={setSelectedRooms}
                  openModal={openModal}
                  step={step}
                  currentHotelIndex={currentHotelIndex}
                  setCurrentHotelIndex={setCurrentHotelIndex}
                />
              )}
              {step > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    if (formRef.current) {
                      formRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      setTimeout(() => {
                        window.scrollBy({
                          top: -75,
                          left: 0,
                          behavior: "smooth",
                        });
                      }, 300);
                    }
                    if (step === 4) {
                      handleInquirySubmit();
                    } else {
                      handleNext();
                    }
                  }}
                  disabled={
                    (step === 1 && !inquiryForm.tripDuration) ||
                    !!inquirySuccess
                  }
                  className="w-full mt-2 px-4 py-2 bg-opera-red text-white rounded text-base font-normal hover:bg-opera-hover disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors lg:hidden"
                >
                  {getButtonText()}
                </button>
              )}
            </div>
            <div className="w-full xl:w-[25%] sticky top-4 self-start">
              <div className="bg-white px-4 pt-4 pb-2 rounded-md border shadow-md hidden lg:block">
                <h3 className="text-lg mb-2">
                  {t("EventPage.reviewAndBooking")}
                </h3>
                <p
                  onClick={() => handleTabClick(1)}
                  className="text-black flex gap-2 items-center mb-2 hover:cursor-pointer"
                >
                  <img
                    src="/images/calendar.jpg"
                    alt="Calendar"
                    className="w-6 h-6 object-cover rounded"
                  />
                  {inquiryForm.tripDuration
                    ? `From ${new Date(
                        inquiryForm.tripDuration.startDate
                      ).toLocaleDateString("en-UK", {
                        month: "short",
                        day: "numeric",
                      })} to ${new Date(
                        inquiryForm.tripDuration.endDate
                      ).toLocaleDateString("en-UK", {
                        month: "short",
                        day: "numeric",
                      })} ${inquiryForm.tripDuration.year} `
                    : t("EventPage.chooseYourDates")}
                </p>
                <p
                  onClick={() => handleTabClick(2)}
                  className="text-black flex gap-2 items-center mb-2 hover:cursor-pointer"
                >
                  <img
                    src="/images/tickets.jpg"
                    alt="Tickets"
                    className="w-6 h-6 object-cover rounded"
                  />
                  {inquiryForm.seatingPreference ||
                    t("EventPage.chooseYourSeating")}
                </p>
                <p
                  onClick={() => handleTabClick(3)}
                  className="text-black flex gap-2 items-center mb-1 hover:cursor-pointer"
                >
                  <img
                    src="/images/hotel.jpg"
                    alt={t("EventPage.hotel")}
                    className="w-6 h-6 object-cover rounded"
                  />
                  {inquiryForm.hotelPreference.length > 0
                    ? Array.from(
                        new Map(
                          inquiryForm.hotelPreference.map((pref) => [
                            pref.roomId,
                            pref,
                          ])
                        ).values()
                      )
                        .map((pref) => `${pref.hotelName} - ${pref.roomType} `)
                        .join(", ")
                    : t("EventPage.chooseYourHotel")}
                </p>
                {inquiryForm.numberOfGuests >= 0 && (
                  <div
                    onClick={() => handleTabClick(2)}
                    className="pl-2 flex items-center gap-1 text-black hover:cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faUser} />
                    <p className="text-black mt-1 ml-2">
                      {inquiryForm.numberOfGuests}
                    </p>
                    <div className="flex-1 mt-1">
                      {inquiryForm.numberOfGuests > 1
                        ? t("EventPage.Guests")
                        : t("EventPage.Guest")}
                    </div>
                  </div>
                )}
                {!isCustomInquiry &&
                  hasBasePrice &&
                  filteredHotels.length !== 0 && (
                    <div>
                      <p className="text-black font-medium mt-1 mb-1">
                        {inquiryForm.tripDuration
                          ? `${t("EventPage.pricePerPerson")}: `
                          : t("EventPage.from") + " "}
                        <span>
                          {getDisplayPrice(
                            inquiryForm.hotelPreference.length,
                            hotelPricesPerPersonStep2,
                            hotelPricesPerPerson
                          )}
                        </span>{" "}
                        {!inquiryForm.tripDuration && t("EventPage.perPerson")}
                      </p>
                      {inquiryForm.hotelPreference.length > 0 && (
                        <p className="text-black font-medium mt-1 mb-1">
                          <strong>
                            {t("EventPage.total")}: €
                            {totalPrice.toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })}
                          </strong>
                        </p>
                      )}
                    </div>
                  )}

                {step > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      step === 4 ? handleInquirySubmit() : handleNext()
                    }
                    disabled={!inquiryForm.tripDuration || !!inquirySuccess}
                    className="w-full mt-1 px-4 py-2 bg-opera-red text-white rounded text-base font-normal hover:bg-opera-hover disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {getButtonText()}
                  </button>
                )}
                <p className="text-sm mt-2 text-center">
                  Your request is non-binding.
                </p>
              </div>
            </div>
          </div>
          <button
            className="mt-4 text-xl text-opera-red font-cinzel font-medium w-full text-left flex justify-between items-center focus:outline-none active:outline-none"
            onClick={() => setInquiryMapOpen(!inquiryMapOpen)}
          >
            Hotel Map
            {inquiryMapOpen ? (
              <svg
                className="w-6 h-6 text-black"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m5 15 7-7 7 7"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-black"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 9-7 7-7-7"
                />
              </svg>
            )}
          </button>
          {inquiryMapOpen && (
            <div className="mt-4">
              {loadError ? (
                <p className="text-center text-red-500">
                  {t("EventPage.errorLoadingMap")}: {loadError.message}
                </p>
              ) : !isLoaded ? (
                <p className="text-center text-black">
                  {t("EventPage.loadingMap")}
                </p>
              ) : (
                <>
                  <h2 className="mb-1">
                    See where your Hotel is located – Click to select & explore
                  </h2>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={defaultCenter}
                    zoom={14}
                  >
                    {venueCoords && (
                      <>
                        <Marker
                          position={venueCoords}
                          icon={{
                            url: "/images/venue_0.png",
                            scaledSize: new window.google.maps.Size(32, 32),
                            anchor: new window.google.maps.Point(16, 32),
                          }}
                          onClick={() => setSelectedVenueMarker(true)}
                        />
                        {event?.venue?.name && selectedVenueMarker && (
                          <OverlayView
                            position={venueCoords}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                          >
                            <div
                              className="bg-white rounded-lg shadow-lg p-2 min-w-[120px] max-w-[220px] border border-gray-200 text-center"
                              style={{
                                transform: "translate(-50%, -120%)",
                                position: "absolute",
                                left: "50%",
                                top: 0,
                                zIndex: 1000,
                              }}
                            >
                              <button
                                className="absolute top-1 right-1 text-gray-400 hover:text-black text-lg font-bold"
                                onClick={() => setSelectedVenueMarker(false)}
                                style={{ lineHeight: 1 }}
                              >
                                ×
                              </button>
                              <span
                                className="text-opera-red text-base font-cinzel transition duration-150 mt-5 block"
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.filter =
                                    "brightness(1.5)";
                                }}
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.filter =
                                    "brightness(1)")
                                }
                              >
                                {event.venue.name}
                              </span>
                            </div>
                          </OverlayView>
                        )}
                      </>
                    )}
                    {filteredHotels.map(
                      (hotel) =>
                        hotelCoords[hotel._id] && (
                          <Marker
                            key={hotel._id}
                            position={hotelCoords[hotel._id]}
                            icon={{
                              url:
                                inquiryForm?.hotelPreference.length === 0
                                  ? "/images/hotel_0.png"
                                  : inquiryForm?.hotelPreference.length > 0 &&
                                    inquiryForm?.hotelPreference[0]?.hotelId ===
                                      hotel._id
                                  ? "/images/hotel_0.png"
                                  : "/images/hotel_gray.png",
                              scaledSize: new window.google.maps.Size(32, 32),
                              anchor: new window.google.maps.Point(16, 32),
                            }}
                            onClick={() => setSelectedHotelMarker(hotel)} // Add this state
                          >
                            {selectedHotelMarker === hotel && (
                              <OverlayView
                                position={hotelCoords[hotel._id]}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                              >
                                <div
                                  className="bg-white rounded-lg shadow-lg p-4 min-w-[180px] max-w-[260px] border border-gray-200"
                                  style={{
                                    transform: "translate(-50%, -110%)",
                                    position: "absolute",
                                    left: "50%",
                                    top: 0,
                                    zIndex: 1000,
                                  }}
                                >
                                  <button
                                    className="absolute top-1 right-1 text-gray-400 hover:text-black text-lg font-bold"
                                    onClick={() => setSelectedHotelMarker(null)}
                                    style={{ lineHeight: 1 }}
                                  >
                                    ×
                                  </button>
                                  <span
                                    className="text-opera-red text-lg font-cinzel transition duration-150"
                                    style={{ cursor: "pointer" }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.filter =
                                        "brightness(1.5)")
                                    }
                                    onClick={() => {
                                      // Switch to hotel selection step, focus the selected hotel
                                      setStep(3);
                                      handleSelectHotelById(hotel._id);
                                      // Scroll booking/request section into view so user sees the hotel list
                                      scrollToBookingSection();
                                    }}
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.filter =
                                        "brightness(1)")
                                    }
                                  >
                                    {hotel.name}
                                  </span>
                                </div>
                              </OverlayView>
                            )}
                          </Marker>
                        )
                    )}
                  </GoogleMap>
                </>
              )}
              {mapError && (
                <p className="text-center text-red-500">{mapError}</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default InquiryForm;
